import { useState, useCallback } from "react";

/* ── GLOBAL STYLES ─────────────────────────────────────── */
const globalStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Noto+Sans+KR:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #ffffff; --bg2: #f7f7f5; --bg3: #f0f0ed;
    --border: #e8e8e4; --border2: #d8d8d4;
    --text: #1a1a1a; --muted: #888; --muted2: #bbb;
    --accent: #2563eb; --accent-light: #eff4ff; --accent-hover: #1d4ed8;
    --danger: #dc2626; --success: #16a34a;
    --hot-bg: #fff0ee; --hot-text: #e5390a;
    --radius: 12px; --radius-sm: 8px; --radius-xs: 6px;
  }
  body { background: var(--bg); color: var(--text); font-family: 'Noto Sans KR', sans-serif; font-weight: 300; -webkit-font-smoothing: antialiased; line-height: 1.6; }
  input, select, button { font-family: inherit; }
  a { color: inherit; text-decoration: none; }

  .tb-nav { position: sticky; top: 0; z-index: 200; background: rgba(255,255,255,0.94); backdrop-filter: blur(14px); border-bottom: 1px solid var(--border); height: 60px; display: flex; align-items: center; padding: 0 2rem; gap: 2rem; }
  .tb-logo { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.15rem; letter-spacing: -0.03em; cursor: pointer; }
  .tb-logo span { color: var(--accent); }
  .tb-nav-links { display: flex; gap: 1.6rem; flex: 1; }
  .tb-nav-links button { background: none; border: none; font-size: 0.82rem; color: var(--muted); cursor: pointer; padding: 0; transition: color 0.15s; font-weight: 400; }
  .tb-nav-links button:hover { color: var(--text); }

  .tb-ad-banner { background: var(--bg2); border-bottom: 1px solid var(--border); padding: 9px; text-align: center; }
  .tb-ad-pill { display: inline-flex; align-items: center; gap: 7px; font-size: 0.7rem; color: var(--muted); font-family: 'Outfit', sans-serif; }
  .tb-ad-tag { background: var(--border); color: var(--muted); font-size: 0.58rem; padding: 1px 5px; border-radius: 3px; font-weight: 600; letter-spacing: 0.04em; }

  .tb-page { max-width: 760px; margin: 0 auto; padding: 2.5rem 1.5rem 6rem; }
  .tb-breadcrumb { font-size: 0.75rem; color: var(--muted); margin-bottom: 1.2rem; display: flex; align-items: center; gap: 0.4rem; }
  .tb-breadcrumb button { background: none; border: none; color: var(--muted); cursor: pointer; font-size: 0.75rem; padding: 0; transition: color 0.15s; }
  .tb-breadcrumb button:hover { color: var(--accent); }
  .tb-page-title { font-family: 'Outfit', sans-serif; font-size: 1.75rem; font-weight: 700; letter-spacing: -0.03em; margin-bottom: 0.35rem; display: flex; align-items: center; gap: 0.5rem; }
  .tb-page-desc { font-size: 0.88rem; color: var(--muted); margin-bottom: 2rem; line-height: 1.65; }

  .tb-card { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 1.6rem; margin-bottom: 1.2rem; }
  .tb-card-white { background: #fff; border: 1px solid var(--border); border-radius: var(--radius); padding: 1.6rem; margin-bottom: 1.2rem; }
  .tb-card-label { font-family: 'Outfit', sans-serif; font-size: 0.65rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); margin-bottom: 1.1rem; }

  .tb-label { display: block; font-size: 0.8rem; color: var(--text); margin-bottom: 0.35rem; font-weight: 400; }
  .tb-input { width: 100%; padding: 0.72rem 0.9rem; border: 1.5px solid var(--border); border-radius: var(--radius-sm); font-size: 0.88rem; background: var(--bg); color: var(--text); outline: none; transition: border-color 0.18s, box-shadow 0.18s; margin-bottom: 0.9rem; }
  .tb-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }
  .tb-select { width: 100%; padding: 0.72rem 0.9rem; border: 1.5px solid var(--border); border-radius: var(--radius-sm); font-size: 0.88rem; background: var(--bg); color: var(--text); outline: none; transition: border-color 0.18s; margin-bottom: 0.9rem; cursor: pointer; }
  .tb-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }

  .tb-btn { display: flex; align-items: center; justify-content: center; width: 100%; padding: 0.8rem 1.6rem; background: var(--accent); color: #fff; border: none; border-radius: var(--radius-sm); font-size: 0.9rem; font-family: 'Outfit', sans-serif; font-weight: 600; cursor: pointer; transition: background 0.15s, transform 0.1s; }
  .tb-btn:hover { background: var(--accent-hover); }
  .tb-btn:active { transform: scale(0.99); }
  .tb-btn-ghost { background: transparent; color: var(--accent); border: 1.5px solid var(--accent); }
  .tb-btn-ghost:hover { background: var(--accent-light); }
  .tb-btn-sm { width: auto; padding: 0.5rem 1.1rem; font-size: 0.8rem; }

  .tb-result { background: var(--accent); color: #fff; border-radius: var(--radius); padding: 1.6rem; margin-top: 1.2rem; }
  .tb-result-label { font-size: 0.68rem; font-weight: 600; opacity: 0.72; letter-spacing: 0.1em; text-transform: uppercase; font-family: 'Outfit', sans-serif; margin-bottom: 0.25rem; }
  .tb-result-value { font-family: 'Outfit', sans-serif; font-size: 2rem; font-weight: 700; letter-spacing: -0.03em; margin-bottom: 0.2rem; line-height: 1.1; }
  .tb-result-sub { font-size: 0.8rem; opacity: 0.72; }
  .tb-result-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-top: 1rem; }
  .tb-result-item { background: rgba(255,255,255,0.15); border-radius: var(--radius-xs); padding: 0.75rem 0.9rem; }
  .tb-result-item-label { font-size: 0.63rem; opacity: 0.7; font-family: 'Outfit', sans-serif; letter-spacing: 0.07em; text-transform: uppercase; margin-bottom: 0.2rem; }
  .tb-result-item-value { font-family: 'Outfit', sans-serif; font-size: 0.95rem; font-weight: 700; }

  .tb-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.9rem; }
  .tb-row3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.9rem; }

  .tb-badge { display: inline-flex; align-items: center; font-size: 0.6rem; font-weight: 700; padding: 2px 7px; border-radius: 100px; font-family: 'Outfit', sans-serif; letter-spacing: 0.04em; }
  .tb-badge-hot { background: var(--hot-bg); color: var(--hot-text); }
  .tb-badge-new { background: var(--accent-light); color: var(--accent); }
  .tb-badge-soon { background: var(--bg3); color: var(--muted); }

  .tb-ad-rect { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius-sm); height: 90px; display: flex; align-items: center; justify-content: center; gap: 8px; margin: 1.8rem 0; font-size: 0.68rem; color: var(--muted2); font-family: 'Outfit', sans-serif; }

  .tb-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
  .tb-table th { text-align: left; padding: 0.55rem 0.9rem; color: var(--muted); font-weight: 500; border-bottom: 1px solid var(--border); background: var(--bg2); }
  .tb-table td { padding: 0.55rem 0.9rem; border-bottom: 1px solid var(--border); }
  .tb-table tr:last-child td { border-bottom: none; }

  .tb-warn { background: #fffbeb; border: 1px solid #fde68a; border-radius: var(--radius-sm); padding: 0.9rem 1.1rem; font-size: 0.8rem; color: #92400e; line-height: 1.6; margin-bottom: 1.2rem; }
  .tb-info { background: var(--accent-light); border: 1px solid #bfdbfe; border-radius: var(--radius-sm); padding: 0.9rem 1.1rem; font-size: 0.8rem; color: #1e40af; line-height: 1.6; margin-bottom: 1.2rem; }

  .tb-progress-bar { background: var(--bg3); border: 1px solid var(--border); border-radius: 100px; height: 10px; overflow: hidden; margin-top: 1.2rem; }
  .tb-progress-fill { height: 100%; background: var(--accent); border-radius: 100px; transition: width 0.6s ease; }

  .tb-tab-group { display: flex; gap: 4px; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 3px; margin-bottom: 1.2rem; }
  .tb-tab { flex: 1; padding: 0.55rem; border: none; border-radius: 6px; font-family: 'Outfit', sans-serif; font-size: 0.82rem; cursor: pointer; transition: all 0.15s; }
  .tb-tab-active { background: var(--accent); color: #fff; font-weight: 600; }
  .tb-tab-inactive { background: transparent; color: var(--muted); }

  .tb-divider { border: none; border-top: 1px solid var(--border); margin: 1.2rem 0; }

  /* HOME */
  .tb-hero { max-width: 680px; margin: 0 auto; padding: 4rem 1.5rem 3rem; text-align: center; }
  .tb-hero-tag { display: inline-block; background: var(--accent-light); color: var(--accent); font-size: 0.72rem; font-weight: 600; padding: 4px 12px; border-radius: 100px; margin-bottom: 1.2rem; font-family: 'Outfit', sans-serif; }
  .tb-hero h1 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: clamp(1.8rem, 4.5vw, 2.8rem); letter-spacing: -0.04em; line-height: 1.15; margin-bottom: 0.9rem; }
  .tb-hero p { font-size: 0.95rem; color: var(--muted); line-height: 1.7; margin-bottom: 1.8rem; }
  .tb-search-box { display: flex; align-items: center; background: #fff; border: 1.5px solid var(--border); border-radius: var(--radius); overflow: hidden; max-width: 460px; margin: 0 auto; transition: border-color 0.18s, box-shadow 0.18s; }
  .tb-search-box:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(37,99,235,0.08); }
  .tb-search-icon { padding: 0 0.9rem; color: var(--muted2); font-size: 0.9rem; }
  .tb-search-input { flex: 1; border: none; outline: none; padding: 0.8rem 0; font-size: 0.88rem; background: transparent; color: var(--text); font-family: 'Noto Sans KR', sans-serif; }
  .tb-search-input::placeholder { color: var(--muted2); }

  .tb-home-content { max-width: 1060px; margin: 0 auto; padding: 0 1.5rem 5rem; display: grid; grid-template-columns: 1fr 260px; gap: 2.5rem; }
  .tb-section-label { font-family: 'Outfit', sans-serif; font-size: 0.65rem; font-weight: 700; letter-spacing: 0.13em; text-transform: uppercase; color: var(--muted); margin-bottom: 0.9rem; padding-bottom: 0.65rem; border-bottom: 1px solid var(--border); }
  .tb-section { margin-bottom: 2.5rem; }

  .tb-featured { background: var(--accent); border-radius: var(--radius); padding: 1.6rem; display: flex; justify-content: space-between; align-items: center; gap: 1.2rem; margin-bottom: 2rem; cursor: pointer; transition: opacity 0.18s; color: #fff; }
  .tb-featured:hover { opacity: 0.93; }
  .tb-featured-tag { font-size: 0.66rem; font-weight: 600; opacity: 0.7; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 0.35rem; font-family: 'Outfit', sans-serif; }
  .tb-featured-title { font-family: 'Outfit', sans-serif; font-size: 1.25rem; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 0.3rem; }
  .tb-featured-desc { font-size: 0.8rem; opacity: 0.72; line-height: 1.5; }
  .tb-featured-cta { background: rgba(255,255,255,0.2); border: 1.5px solid rgba(255,255,255,0.38); color: #fff; padding: 0.55rem 1.1rem; border-radius: var(--radius-sm); font-size: 0.8rem; font-weight: 600; white-space: nowrap; flex-shrink: 0; font-family: 'Outfit', sans-serif; cursor: pointer; border-width: 1.5px; }

  .tb-tools-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; }
  .tb-tool-card { background: #fff; padding: 1.2rem; cursor: pointer; transition: background 0.13s; display: flex; flex-direction: column; gap: 0.3rem; }
  .tb-tool-card:hover { background: var(--bg2); }
  .tb-tool-icon { font-size: 1.4rem; margin-bottom: 0.25rem; }
  .tb-tool-name { font-size: 0.86rem; font-weight: 500; color: var(--text); font-family: 'Outfit', sans-serif; line-height: 1.3; }
  .tb-tool-desc { font-size: 0.74rem; color: var(--muted); line-height: 1.45; }

  .tb-sidebar-ad { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius-sm); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; font-size: 0.68rem; color: var(--muted2); font-family: 'Outfit', sans-serif; }

  .tb-footer { border-top: 1px solid var(--border); background: var(--bg2); }
  .tb-footer-inner { max-width: 1060px; margin: 0 auto; padding: 2.5rem 1.5rem; display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 2.5rem; }
  .tb-footer-logo { font-family: 'Outfit', sans-serif; font-size: 1rem; font-weight: 700; margin-bottom: 0.4rem; }
  .tb-footer-logo span { color: var(--accent); }
  .tb-footer-desc { font-size: 0.78rem; color: var(--muted); line-height: 1.7; }
  .tb-footer-heading { font-size: 0.66rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--muted); margin-bottom: 0.8rem; font-family: 'Outfit', sans-serif; }
  .tb-footer-links { list-style: none; display: flex; flex-direction: column; gap: 0.5rem; }
  .tb-footer-links button { background: none; border: none; font-size: 0.82rem; color: var(--text); cursor: pointer; padding: 0; text-align: left; transition: color 0.15s; font-family: 'Noto Sans KR', sans-serif; font-weight: 300; }
  .tb-footer-links button:hover { color: var(--accent); }
  .tb-footer-bottom { border-top: 1px solid var(--border); padding: 0.9rem 1.5rem; max-width: 1060px; margin: 0 auto; display: flex; justify-content: space-between; }
  .tb-footer-copy { font-size: 0.7rem; color: var(--muted); font-family: 'Outfit', sans-serif; }

  @media (max-width: 800px) { .tb-home-content { grid-template-columns: 1fr; } .tb-sidebar { display: none; } }
  @media (max-width: 560px) { .tb-row2, .tb-row3 { grid-template-columns: 1fr; } .tb-result-grid { grid-template-columns: 1fr; } .tb-footer-inner { grid-template-columns: 1fr; } }

  /* color converter specific */
  .color-preview { height: 120px; border-radius: var(--radius); margin-bottom: 1.2rem; transition: background 0.2s; display: flex; align-items: center; justify-content: center; font-family: 'Outfit', sans-serif; font-size: 0.82rem; }
  .swatch { width: 34px; height: 34px; border-radius: 7px; cursor: pointer; border: 2px solid rgba(0,0,0,0.06); transition: transform 0.1s; flex-shrink: 0; }
  .swatch:hover { transform: scale(1.15); }
  .css-output { font-family: 'JetBrains Mono', 'Courier New', monospace; font-size: 0.76rem; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 0.9rem; line-height: 1.9; white-space: pre; overflow-x: auto; }
`;

/* ── TOOLS DATA ─────────────────────────────────────────── */
const TOOLS = [
  { id: "salary",   icon: "💰", name: "연봉 실수령액 계산기", desc: "세전 연봉 → 세후 월급", category: "calculators", badge: "hot" },
  { id: "military", icon: "🪖", name: "전역일 계산기",         desc: "입대일 기준 D-day 계산",  category: "calculators", badge: "hot" },
  { id: "bmi",      icon: "⚖️", name: "BMI 계산기",            desc: "신장·체중으로 체질량 확인", category: "calculators", badge: "new" },
  { id: "date",     icon: "📅", name: "날짜 계산기",           desc: "두 날짜 사이 간격 계산",   category: "calculators" },
  { id: "loan",     icon: "🏠", name: "대출 이자 계산기",      desc: "원리금균등·원금균등 비교", category: "calculators" },
  { id: "age",      icon: "🎂", name: "나이 계산기",           desc: "만 나이·한국 나이·띠",     category: "calculators" },
  { id: "exchange", icon: "💱", name: "환율 변환기",           desc: "13개 통화 환율 변환",       category: "converters" },
  { id: "unit",     icon: "📏", name: "단위 변환기",           desc: "길이·무게·온도·넓이",       category: "converters" },
  { id: "color",    icon: "🎨", name: "색상 코드 변환기",      desc: "HEX ↔ RGB ↔ HSL",          category: "converters", badge: "new" },
  { id: "filesize", icon: "💾", name: "파일 용량 변환기",      desc: "bit ~ TB 단위 변환",        category: "converters" },
  { id: "instagram",icon: "📸", name: "인스타그램 익명 뷰어", desc: "공개 계정 프로필 열람",     category: "social", external: "/instagram" },
  { id: "youtube",  icon: "🎬", name: "유튜브 썸네일 다운로더",desc: "URL → HD 썸네일 저장",     category: "social" },
];

/* ── SMALL COMPONENTS ───────────────────────────────────── */
const AdBanner = () => (
  <div className="tb-ad-banner">
    <div className="tb-ad-pill"><span className="tb-ad-tag">AD</span> Google AdSense — 728×90</div>
  </div>
);

const AdRect = ({ label = "336×280 Rectangle" }) => (
  <div className="tb-ad-rect"><span className="tb-ad-tag">AD</span>{label}</div>
);

const Breadcrumb = ({ items, onNav }) => (
  <div className="tb-breadcrumb">
    {items.map((item, i) => (
      <span key={i} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        {i > 0 && <span style={{ color: "var(--muted2)" }}>/</span>}
        {item.page ? (
          <button onClick={() => onNav(item.page)}>{item.label}</button>
        ) : (
          <span>{item.label}</span>
        )}
      </span>
    ))}
  </div>
);

const Badge = ({ type }) => {
  if (!type) return null;
  const map = { hot: ["tb-badge tb-badge-hot", "인기"], new: ["tb-badge tb-badge-new", "NEW"], soon: ["tb-badge tb-badge-soon", "준비중"] };
  const [cls, txt] = map[type] || [];
  return <span className={cls}>{txt}</span>;
};

const ResultItem = ({ label, value }) => (
  <div className="tb-result-item">
    <div className="tb-result-item-label">{label}</div>
    <div className="tb-result-item-value">{value}</div>
  </div>
);

const fmt = (n) => Math.round(n).toLocaleString("ko-KR") + "원";

/* ═══════════════════════════════════════════════════════════
   TOOL PAGES
═══════════════════════════════════════════════════════════ */

/* ── 연봉 계산기 ─────────────────────────────────────────── */
function SalaryPage({ onNav }) {
  const [salary, setSalary] = useState("");
  const [dep, setDep] = useState("1");
  const [children, setChildren] = useState("0");
  const [result, setResult] = useState(null);

  function incomeTax(monthly, dep, children) {
    let tax = 0;
    if (monthly <= 1060000) tax = 0;
    else if (monthly <= 1500000) tax = (monthly - 1060000) * 0.06;
    else if (monthly <= 3000000) tax = 26400 + (monthly - 1500000) * 0.15;
    else if (monthly <= 4500000) tax = 251400 + (monthly - 3000000) * 0.24;
    else if (monthly <= 8000000) tax = 611400 + (monthly - 4500000) * 0.35;
    else tax = 1836400 + (monthly - 8000000) * 0.38;
    tax = Math.max(0, tax - dep * 12500 - (children >= 3 ? children * 20000 : children * 12500));
    return Math.round(tax / 10) * 10;
  }

  function calc() {
    const annual = parseFloat(salary);
    if (!annual || annual <= 0) return;
    const monthly = Math.round((annual * 10000) / 12);
    const pension = Math.round(Math.min(monthly, 5900000) * 0.045);
    const health  = Math.round(monthly * 0.03545);
    const ltc     = Math.round(health * 0.1295);
    const employ  = Math.round(monthly * 0.009);
    const income  = incomeTax(monthly, parseInt(dep), parseInt(children));
    const local   = Math.round(income * 0.1);
    const total   = pension + health + ltc + employ + income + local;
    setResult({ monthly, pension, health, ltc, employ, income, local, total, net: monthly - total });
  }

  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "계산기" }, { label: "연봉 실수령액" }]} onNav={onNav} />
      <h1 className="tb-page-title">연봉 실수령액 계산기 <Badge type="hot" /></h1>
      <p className="tb-page-desc">세전 연봉을 입력하면 4대보험과 소득세를 공제한 월 실수령액을 계산합니다.</p>
      <div className="tb-card">
        <div className="tb-card-label">입력</div>
        <label className="tb-label">세전 연봉 (만원)</label>
        <input className="tb-input" type="number" placeholder="예: 4000" value={salary} onChange={e => setSalary(e.target.value)} onKeyDown={e => e.key === "Enter" && calc()} />
        <div className="tb-row2">
          <div>
            <label className="tb-label">부양가족 수 (본인 포함)</label>
            <select className="tb-select" value={dep} onChange={e => setDep(e.target.value)}>
              {["1명 (본인만)", "2명", "3명", "4명", "5명 이상"].map((v, i) => <option key={i} value={i + 1}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="tb-label">20세 이하 자녀 수</label>
            <select className="tb-select" value={children} onChange={e => setChildren(e.target.value)}>
              {["없음", "1명", "2명", "3명 이상"].map((v, i) => <option key={i} value={i}>{v}</option>)}
            </select>
          </div>
        </div>
        <button className="tb-btn" onClick={calc}>계산하기</button>
      </div>
      {result && (
        <div className="tb-result">
          <div className="tb-result-label">월 실수령액</div>
          <div className="tb-result-value">{fmt(result.net)}</div>
          <div className="tb-result-sub">연 실수령액 {fmt(result.net * 12)} · 월 공제 합계 {fmt(result.total)}</div>
          <div className="tb-result-grid">
            {[["국민연금", result.pension], ["건강보험", result.health], ["장기요양보험", result.ltc], ["고용보험", result.employ], ["소득세", result.income], ["지방소득세", result.local]].map(([l, v]) => <ResultItem key={l} label={l} value={fmt(v)} />)}
          </div>
        </div>
      )}
      <AdRect />
      <div className="tb-card-white">
        <div className="tb-card-label">계산 기준 안내</div>
        <ul style={{ fontSize: "0.8rem", color: "var(--muted)", lineHeight: 2.1, paddingLeft: "1.1rem" }}>
          <li>국민연금: 월 소득의 4.5% (상한 590만원)</li>
          <li>건강보험: 월 소득의 3.545%</li>
          <li>장기요양보험: 건강보험료의 12.95%</li>
          <li>고용보험: 월 소득의 0.9%</li>
          <li>소득세: 2024년 근로소득 간이세액표 기준</li>
          <li>지방소득세: 소득세의 10%</li>
        </ul>
      </div>
    </div>
  );
}

/* ── 전역일 계산기 ───────────────────────────────────────── */
function MilitaryPage({ onNav }) {
  const today = new Date().toISOString().split("T")[0];
  const [branch, setBranch] = useState("18");
  const [enlist, setEnlist] = useState(today);
  const [result, setResult] = useState(null);

  const DAYS_KO = ["일", "월", "화", "수", "목", "금", "토"];
  const fmtDate = d => d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "short" });

  function calc() {
    const enlistDate = new Date(enlist);
    const discharge = new Date(enlistDate);
    discharge.setMonth(discharge.getMonth() + parseInt(branch));
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const totalDays = Math.round((discharge - enlistDate) / 86400000);
    const servedDays = Math.max(0, Math.min(Math.round((now - enlistDate) / 86400000), totalDays));
    const remainDays = Math.max(0, Math.round((discharge - now) / 86400000));
    const pct = Math.min(100, Math.round((servedDays / totalDays) * 100));
    setResult({ discharge, enlistDate, totalDays, servedDays, remainDays, pct, months: branch, isDischarged: now >= discharge });
  }

  const BRANCHES = [["18", "육군 / 해병대 (18개월)"], ["20", "해군 (20개월)"], ["21", "공군 (21개월)"], ["21", "사회복무요원 (21개월)"], ["24", "전문연구요원 (24개월)"]];

  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "계산기" }, { label: "전역일" }]} onNav={onNav} />
      <h1 className="tb-page-title">전역일 계산기 <Badge type="hot" /></h1>
      <p className="tb-page-desc">입대일과 군종을 선택하면 전역일과 남은 날짜를 자동으로 계산합니다.</p>
      <div className="tb-card">
        <div className="tb-card-label">입력</div>
        <label className="tb-label">군종 선택</label>
        <select className="tb-select" value={branch} onChange={e => setBranch(e.target.value)}>
          {BRANCHES.map(([v, l], i) => <option key={i} value={v}>{l}</option>)}
        </select>
        <label className="tb-label">입대일</label>
        <input className="tb-input" type="date" value={enlist} onChange={e => setEnlist(e.target.value)} />
        <button className="tb-btn" onClick={calc}>계산하기</button>
      </div>
      {result && (
        <>
          <div className="tb-result">
            <div className="tb-result-label">전역일</div>
            <div className="tb-result-value">{fmtDate(result.discharge)}</div>
            <div className="tb-result-sub">{result.isDischarged ? "✅ 이미 전역하셨습니다!" : `전역까지 D-${result.remainDays}일`}</div>
            <div className="tb-result-grid">
              {[["입대일", fmtDate(result.enlistDate)], ["복무 기간", `${result.months}개월`], ["총 복무일", `${result.totalDays.toLocaleString()}일`], ["현재까지 복무", `${result.servedDays.toLocaleString()}일`], ["복무 진행률", `${result.pct}%`], ["남은 일수", result.isDischarged ? "전역 완료" : `${result.remainDays.toLocaleString()}일`]].map(([l, v]) => <ResultItem key={l} label={l} value={v} />)}
            </div>
          </div>
          <div style={{ padding: "0 0.2rem", marginTop: "1.2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.74rem", color: "var(--muted)", marginBottom: "0.4rem" }}>
              <span>입대</span><span>전역</span>
            </div>
            <div className="tb-progress-bar"><div className="tb-progress-fill" style={{ width: result.pct + "%" }} /></div>
          </div>
        </>
      )}
      <AdRect />
    </div>
  );
}

/* ── BMI 계산기 ─────────────────────────────────────────── */
function BmiPage({ onNav }) {
  const [h, setH] = useState(""); const [w, setW] = useState(""); const [result, setResult] = useState(null);
  function calc() {
    const hv = parseFloat(h), wv = parseFloat(w);
    if (!hv || !wv) return;
    const bmi = wv / (hv / 100) ** 2;
    const status = bmi < 18.5 ? "저체중" : bmi < 23 ? "정상" : bmi < 25 ? "과체중" : bmi < 30 ? "비만" : "고도비만";
    const ideal = 22 * (hv / 100) ** 2;
    const normalMin = 18.5 * (hv / 100) ** 2, normalMax = 22.9 * (hv / 100) ** 2;
    const pct = Math.min(100, Math.max(0, ((bmi - 13) / 22) * 100));
    setResult({ bmi: bmi.toFixed(1), status, ideal: ideal.toFixed(1), diff: (wv - ideal).toFixed(1), range: `${normalMin.toFixed(1)}~${normalMax.toFixed(1)}kg`, pct });
  }
  const BMI_LABELS = [["저체중", "#3b82f6", 2], ["정상", "#22c55e", 3], ["과체중", "#f59e0b", 2], ["비만", "#f97316", 2], ["고도비만", "#ef4444", 1.5]];
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "계산기" }, { label: "BMI" }]} onNav={onNav} />
      <h1 className="tb-page-title">BMI 계산기 <Badge type="new" /></h1>
      <p className="tb-page-desc">신장과 체중을 입력하면 BMI 지수와 비만도를 즉시 확인할 수 있습니다.</p>
      <div className="tb-card">
        <div className="tb-card-label">입력</div>
        <div className="tb-row2">
          <div><label className="tb-label">신장 (cm)</label><input className="tb-input" type="number" placeholder="예: 175" value={h} onChange={e => setH(e.target.value)} /></div>
          <div><label className="tb-label">체중 (kg)</label><input className="tb-input" type="number" placeholder="예: 70" value={w} onChange={e => setW(e.target.value)} /></div>
        </div>
        <button className="tb-btn" onClick={calc}>계산하기</button>
      </div>
      {result && (
        <>
          <div className="tb-result">
            <div className="tb-result-label">내 BMI 지수</div>
            <div className="tb-result-value">{result.bmi}</div>
            <div className="tb-result-sub">판정: {result.status}</div>
            <div className="tb-result-grid">
              {[["표준 체중", result.ideal + "kg"], ["체중 차이", (result.diff > 0 ? "+" : "") + result.diff + "kg"], ["정상 범위", result.range], ["상태", result.status]].map(([l, v]) => <ResultItem key={l} label={l} value={v} />)}
            </div>
          </div>
          <div style={{ marginTop: "1.2rem" }}>
            <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", height: "26px" }}>
              {BMI_LABELS.map(([lbl, color, flex]) => (
                <div key={lbl} style={{ flex, background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.62rem", fontWeight: 600, fontFamily: "Outfit" }}>{lbl}</div>
              ))}
            </div>
            <div style={{ position: "relative", height: "18px", marginTop: "3px" }}>
              <span style={{ position: "absolute", left: result.pct + "%", transform: "translateX(-50%)", fontSize: "0.9rem", color: "var(--text)" }}>▲</span>
            </div>
          </div>
        </>
      )}
      <AdRect />
      <div className="tb-card-white">
        <div className="tb-card-label">BMI 기준표 (아시아 기준)</div>
        <table className="tb-table">
          <thead><tr><th>판정</th><th>BMI 범위</th></tr></thead>
          <tbody>{[["저체중","18.5 미만"],["정상","18.5 ~ 22.9"],["과체중","23.0 ~ 24.9"],["비만","25.0 ~ 29.9"],["고도비만","30.0 이상"]].map(([s,r])=><tr key={s}><td>{s}</td><td>{r}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

/* ── 날짜 계산기 ─────────────────────────────────────────── */
function DateCalcPage({ onNav }) {
  const today = new Date().toISOString().split("T")[0];
  const [tab, setTab] = useState(0);
  const [d1, setD1] = useState(new Date(Date.now() - 86400000 * 30).toISOString().split("T")[0]);
  const [d2, setD2] = useState(today);
  const [base, setBase] = useState(today);
  const [addDays, setAddDays] = useState(""); const [addMonths, setAddMonths] = useState("");
  const [r0, setR0] = useState(null); const [r1, setR1] = useState(null);
  const DAYS = ["일","월","화","수","목","금","토"];
  const fmtDate = d => d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });

  function calcDiff() {
    const a = new Date(d1), b = new Date(d2);
    const days = Math.abs(Math.round((b - a) / 86400000));
    setR0({ days, weeks: (days / 7).toFixed(1), months: (days / 30.44).toFixed(1), wd1: DAYS[a.getDay()], wd2: DAYS[b.getDay()], from: fmtDate(a < b ? a : b), to: fmtDate(a < b ? b : a) });
  }
  function calcAdd() {
    const d = new Date(base);
    d.setMonth(d.getMonth() + (parseInt(addMonths) || 0));
    d.setDate(d.getDate() + (parseInt(addDays) || 0));
    setR1({ date: fmtDate(d), wd: DAYS[d.getDay()] + "요일" });
  }
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "계산기" }, { label: "날짜" }]} onNav={onNav} />
      <h1 className="tb-page-title">날짜 계산기</h1>
      <p className="tb-page-desc">두 날짜 사이의 간격을 계산하거나, 날짜에 일수를 더하고 뺄 수 있습니다.</p>
      <div className="tb-tab-group">
        {["날짜 간격 계산", "날짜 더하기/빼기"].map((t, i) => (
          <button key={i} className={`tb-tab ${tab === i ? "tb-tab-active" : "tb-tab-inactive"}`} onClick={() => setTab(i)}>{t}</button>
        ))}
      </div>
      {tab === 0 ? (
        <div className="tb-card">
          <div className="tb-card-label">두 날짜 사이 간격</div>
          <div className="tb-row2">
            <div><label className="tb-label">시작 날짜</label><input className="tb-input" type="date" value={d1} onChange={e => setD1(e.target.value)} /></div>
            <div><label className="tb-label">끝 날짜</label><input className="tb-input" type="date" value={d2} onChange={e => setD2(e.target.value)} /></div>
          </div>
          <button className="tb-btn" onClick={calcDiff}>계산하기</button>
          {r0 && (
            <div className="tb-result" style={{ marginTop: "1rem" }}>
              <div className="tb-result-label">두 날짜 사이</div>
              <div className="tb-result-value">{r0.days.toLocaleString()}일</div>
              <div className="tb-result-sub">{r0.from} → {r0.to}</div>
              <div className="tb-result-grid">
                {[["주", r0.weeks + "주"], ["개월 (근사)", r0.months + "개월"], ["시작 요일", r0.wd1 + "요일"], ["끝 요일", r0.wd2 + "요일"]].map(([l, v]) => <ResultItem key={l} label={l} value={v} />)}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="tb-card">
          <div className="tb-card-label">날짜 더하기 / 빼기</div>
          <label className="tb-label">기준 날짜</label>
          <input className="tb-input" type="date" value={base} onChange={e => setBase(e.target.value)} />
          <div className="tb-row2">
            <div><label className="tb-label">일수 (음수 입력 시 빼기)</label><input className="tb-input" type="number" value={addDays} onChange={e => setAddDays(e.target.value)} /></div>
            <div><label className="tb-label">개월 수 (음수 입력 시 빼기)</label><input className="tb-input" type="number" value={addMonths} onChange={e => setAddMonths(e.target.value)} /></div>
          </div>
          <button className="tb-btn" onClick={calcAdd}>계산하기</button>
          {r1 && (
            <div className="tb-result" style={{ marginTop: "1rem" }}>
              <div className="tb-result-label">결과 날짜</div>
              <div className="tb-result-value">{r1.date}</div>
              <div className="tb-result-sub">{r1.wd}</div>
            </div>
          )}
        </div>
      )}
      <AdRect />
    </div>
  );
}

/* ── 대출 계산기 ─────────────────────────────────────────── */
function LoanPage({ onNav }) {
  const [principal, setPrincipal] = useState(""); const [rate, setRate] = useState(""); const [term, setTerm] = useState(""); const [method, setMethod] = useState("equal");
  const [result, setResult] = useState(null); const [rows, setRows] = useState([]);
  const fmtW = n => Math.round(n).toLocaleString("ko-KR") + "원";
  function calc() {
    const P = parseFloat(principal) * 10000, r = parseFloat(rate) / 100 / 12, n = parseInt(term);
    if (!P || !r || !n) return;
    let firstPayment, totalInterest, newRows = [];
    if (method === "equal") {
      const M = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      firstPayment = M; let bal = P;
      for (let i = 1; i <= Math.min(n, 12); i++) {
        const int = bal * r, prin = M - int; bal -= prin;
        newRows.push([i, M, prin, int, Math.max(0, bal)]);
      }
      totalInterest = M * n - P;
    } else if (method === "principal") {
      const pp = P / n; let bal = P, ti = 0;
      firstPayment = pp + bal * r;
      for (let i = 1; i <= Math.min(n, 12); i++) { const int = bal * r; ti += int; newRows.push([i, pp + int, pp, int, Math.max(0, bal - pp)]); bal -= pp; }
      let b2 = P, ti2 = 0; for (let i = 1; i <= n; i++) { ti2 += b2 * r; b2 -= P / n; }
      totalInterest = ti2;
    } else {
      firstPayment = P * r; totalInterest = P * r * n;
      for (let i = 1; i <= Math.min(n, 12); i++) newRows.push([i, i === n ? P + P * r : P * r, i === n ? P : 0, P * r, i === n ? 0 : P]);
    }
    setResult({ firstPayment, totalInterest, totalPayment: P + totalInterest, principal: P, rate, term: n, method });
    setRows(newRows);
  }
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "계산기" }, { label: "대출 이자" }]} onNav={onNav} />
      <h1 className="tb-page-title">대출 이자 계산기</h1>
      <p className="tb-page-desc">대출금액, 금리, 기간을 입력하면 월 상환액과 총 이자를 계산합니다.</p>
      <div className="tb-card">
        <div className="tb-card-label">입력</div>
        <label className="tb-label">대출 금액 (만원)</label>
        <input className="tb-input" type="number" placeholder="예: 30000" value={principal} onChange={e => setPrincipal(e.target.value)} />
        <div className="tb-row2">
          <div><label className="tb-label">연 이자율 (%)</label><input className="tb-input" type="number" step="0.1" placeholder="예: 4.5" value={rate} onChange={e => setRate(e.target.value)} /></div>
          <div><label className="tb-label">대출 기간 (개월)</label><input className="tb-input" type="number" placeholder="예: 360" value={term} onChange={e => setTerm(e.target.value)} /></div>
        </div>
        <label className="tb-label">상환 방식</label>
        <select className="tb-select" value={method} onChange={e => setMethod(e.target.value)}>
          <option value="equal">원리금균등상환</option>
          <option value="principal">원금균등상환</option>
          <option value="bullet">만기일시상환</option>
        </select>
        <button className="tb-btn" onClick={calc}>계산하기</button>
      </div>
      {result && (
        <>
          <div className="tb-result">
            <div className="tb-result-label">월 납입액{method === "principal" ? " (첫 달)" : " (고정)"}</div>
            <div className="tb-result-value">{fmtW(result.firstPayment)}</div>
            <div className="tb-result-sub">연 {result.rate}% · {result.term}개월</div>
            <div className="tb-result-grid">
              {[["대출 원금", fmtW(result.principal)], ["총 이자", fmtW(result.totalInterest)], ["총 납입액", fmtW(result.totalPayment)], ["이자 비율", ((result.totalInterest / result.totalPayment) * 100).toFixed(1) + "%"]].map(([l, v]) => <ResultItem key={l} label={l} value={v} />)}
            </div>
          </div>
          {rows.length > 0 && (
            <div className="tb-card-white" style={{ overflow: "hidden", padding: 0 }}>
              <div style={{ padding: "1rem 1.4rem", borderBottom: "1px solid var(--border)" }}><div className="tb-card-label" style={{ marginBottom: 0 }}>상환 스케줄 (첫 12개월)</div></div>
              <div style={{ overflowX: "auto" }}>
                <table className="tb-table">
                  <thead><tr><th>회차</th><th style={{ textAlign: "right" }}>납입액</th><th style={{ textAlign: "right" }}>원금</th><th style={{ textAlign: "right" }}>이자</th><th style={{ textAlign: "right" }}>잔액</th></tr></thead>
                  <tbody>
                    {rows.map(([i, pay, prin, int, bal]) => (
                      <tr key={i}>
                        <td style={{ color: "var(--muted)" }}>{i}</td>
                        <td style={{ textAlign: "right" }}>{fmtW(pay)}</td>
                        <td style={{ textAlign: "right", color: "var(--accent)" }}>{fmtW(prin)}</td>
                        <td style={{ textAlign: "right", color: "var(--hot-text)" }}>{fmtW(int)}</td>
                        <td style={{ textAlign: "right", color: "var(--muted)" }}>{fmtW(bal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      <AdRect />
    </div>
  );
}

/* ── 나이 계산기 ─────────────────────────────────────────── */
function AgePage({ onNav }) {
  const [birth, setBirth] = useState("1995-01-01");
  const [base, setBase] = useState(new Date().toISOString().split("T")[0]);
  const [result, setResult] = useState(null);
  const DAYS = ["일","월","화","수","목","금","토"];
  const ZODIAC = ["원숭이","닭","개","돼지","쥐","소","호랑이","토끼","용","뱀","말","양"];
  const fmtDate = d => d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });
  function calc() {
    const b = new Date(birth), bs = new Date(base);
    let age = bs.getFullYear() - b.getFullYear();
    if (bs.getMonth() < b.getMonth() || (bs.getMonth() === b.getMonth() && bs.getDate() < b.getDate())) age--;
    const totalDays = Math.round((bs - b) / 86400000);
    let nextBday = new Date(bs.getFullYear(), b.getMonth(), b.getDate());
    if (nextBday <= bs) nextBday.setFullYear(nextBday.getFullYear() + 1);
    const daysToNext = Math.round((nextBday - bs) / 86400000);
    setResult({ age, korean: bs.getFullYear() - b.getFullYear() + 1, year: bs.getFullYear() - b.getFullYear(), totalDays, daysToNext, wd: DAYS[b.getDay()], zodiac: ZODIAC[b.getFullYear() % 12], birthStr: fmtDate(b) });
  }
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "계산기" }, { label: "나이" }]} onNav={onNav} />
      <h1 className="tb-page-title">나이 계산기</h1>
      <p className="tb-page-desc">생년월일을 입력하면 만 나이, 한국 나이, 연 나이를 모두 계산합니다.</p>
      <div className="tb-card">
        <div className="tb-card-label">입력</div>
        <label className="tb-label">생년월일</label>
        <input className="tb-input" type="date" value={birth} onChange={e => setBirth(e.target.value)} />
        <label className="tb-label">기준 날짜 (기본: 오늘)</label>
        <input className="tb-input" type="date" value={base} onChange={e => setBase(e.target.value)} />
        <button className="tb-btn" onClick={calc}>계산하기</button>
      </div>
      {result && (
        <div className="tb-result">
          <div className="tb-result-label">만 나이</div>
          <div className="tb-result-value">{result.age}세</div>
          <div className="tb-result-sub">{result.birthStr} 생</div>
          <div className="tb-result-grid">
            {[["한국 나이", result.korean + "세"], ["연 나이", result.year + "세"], ["태어난 지", result.totalDays.toLocaleString() + "일"], ["다음 생일까지", result.daysToNext === 0 ? "🎉 오늘!" : result.daysToNext + "일 후"], ["태어난 요일", result.wd + "요일"], ["띠", result.zodiac + "띠"]].map(([l, v]) => <ResultItem key={l} label={l} value={v} />)}
          </div>
        </div>
      )}
      <AdRect />
    </div>
  );
}

/* ── 환율 변환기 ─────────────────────────────────────────── */
const RATES = { KRW:1380,USD:1,JPY:150,EUR:0.92,CNY:7.24,GBP:0.79,AUD:1.53,CAD:1.36,HKD:7.82,SGD:1.34,CHF:0.89,VND:24800,THB:35.5 };
const SYMBOLS = { KRW:"₩",USD:"$",JPY:"¥",EUR:"€",CNY:"¥",GBP:"£",AUD:"A$",CAD:"C$",HKD:"HK$",SGD:"S$",CHF:"Fr",VND:"₫",THB:"฿" };
const CURRENCY_LABELS = { KRW:"🇰🇷 KRW — 한국 원",USD:"🇺🇸 USD — 미국 달러",JPY:"🇯🇵 JPY — 일본 엔",EUR:"🇪🇺 EUR — 유로",CNY:"🇨🇳 CNY — 중국 위안",GBP:"🇬🇧 GBP — 영국 파운드",AUD:"🇦🇺 AUD — 호주 달러",CAD:"🇨🇦 CAD — 캐나다 달러",HKD:"🇭🇰 HKD — 홍콩 달러",SGD:"🇸🇬 SGD — 싱가포르 달러",CHF:"🇨🇭 CHF — 스위스 프랑",VND:"🇻🇳 VND — 베트남 동",THB:"🇹🇭 THB — 태국 바트" };
function fmtCur(n, cur) { return (cur==="JPY"||cur==="KRW"||cur==="VND") ? SYMBOLS[cur] + Math.round(n).toLocaleString() : SYMBOLS[cur] + n.toLocaleString("ko-KR",{minimumFractionDigits:2,maximumFractionDigits:4}); }
function getRate(from, to) { return (to_usd => to_usd * RATES[to])(1 / RATES[from]); }

function ExchangePage({ onNav }) {
  const [amount, setAmount] = useState("1"); const [from, setFrom] = useState("USD"); const [to, setTo] = useState("KRW");
  const [result, setResult] = useState(null);
  function convert() {
    const a = parseFloat(amount); if (!a) return;
    const rate = getRate(from, to), res = a * rate;
    setResult({ rate, res, reverseRate: getRate(to, from), from, to, amount: a });
  }
  function swap() { const tmp = from; setFrom(to); setTo(tmp); }
  const PAIRS = [["USD","KRW"],["JPY","KRW"],["EUR","KRW"],["CNY","KRW"],["GBP","KRW"]];
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "변환기" }, { label: "환율" }]} onNav={onNav} />
      <h1 className="tb-page-title">환율 변환기</h1>
      <p className="tb-page-desc">13개 주요 통화 간 환율을 변환합니다. (참고용 환율 기준)</p>
      <div className="tb-card">
        <div className="tb-card-label">변환</div>
        <div className="tb-row2">
          <div><label className="tb-label">금액</label><input className="tb-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} /></div>
          <div><label className="tb-label">통화</label><select className="tb-select" value={from} onChange={e => setFrom(e.target.value)}>{Object.entries(CURRENCY_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
        </div>
        <div style={{ textAlign: "center", margin: "-0.4rem 0 0.5rem" }}>
          <button onClick={swap} style={{ background: "none", border: "1.5px solid var(--border)", borderRadius: "50%", width: "34px", height: "34px", cursor: "pointer", fontSize: "1rem", color: "var(--muted)" }}>⇅</button>
        </div>
        <label className="tb-label">변환 대상</label>
        <select className="tb-select" value={to} onChange={e => setTo(e.target.value)}>{Object.entries(CURRENCY_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select>
        <button className="tb-btn" onClick={convert}>변환하기</button>
      </div>
      {result && (
        <div className="tb-result">
          <div className="tb-result-label">{result.from} → {result.to}</div>
          <div className="tb-result-value">{fmtCur(result.res, result.to)}</div>
          <div className="tb-result-sub">{fmtCur(result.amount, result.from)} = {fmtCur(result.res, result.to)}</div>
          <div className="tb-result-grid">
            <ResultItem label="환율" value={`1 ${result.from} = ${fmtCur(result.rate, result.to)}`} />
            <ResultItem label="역환율" value={`1 ${result.to} = ${fmtCur(result.reverseRate, result.from)}`} />
          </div>
        </div>
      )}
      <AdRect />
      <div className="tb-card-white">
        <div className="tb-card-label">주요 환율표 (KRW 기준)</div>
        <div style={{ borderRadius: "var(--radius-sm)", overflow: "hidden", border: "1px solid var(--border)" }}>
          {PAIRS.map(([f, t], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.7rem 1rem", borderBottom: i < PAIRS.length - 1 ? "1px solid var(--border)" : "none", background: "#fff" }}>
              <span style={{ fontSize: "0.82rem", fontWeight: 500 }}>{SYMBOLS[f]} {f} / {t}</span>
              <span style={{ fontFamily: "Outfit", fontWeight: 700, color: "var(--accent)", fontSize: "0.9rem" }}>{Math.round(getRate(f, t)).toLocaleString()}원</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: "0.7rem" }}>* 참고용 환율입니다. 실제 환율과 차이가 있을 수 있습니다.</p>
      </div>
    </div>
  );
}

/* ── 단위 변환기 ─────────────────────────────────────────── */
const UNIT_CATS = {
  "길이": { units: { mm:0.001,cm:0.01,m:1,km:1000,inch:0.0254,ft:0.3048,yard:0.9144,mile:1609.344 }, labels: { mm:"밀리미터(mm)",cm:"센티미터(cm)",m:"미터(m)",km:"킬로미터(km)",inch:"인치(in)",ft:"피트(ft)",yard:"야드(yd)",mile:"마일(mi)" } },
  "무게": { units: { mg:0.000001,g:0.001,kg:1,t:1000,lb:0.45359,oz:0.028349 }, labels: { mg:"밀리그램(mg)",g:"그램(g)",kg:"킬로그램(kg)",t:"톤(t)",lb:"파운드(lb)",oz:"온스(oz)" } },
  "온도": { special: true },
  "넓이": { units: { cm2:0.0001,m2:1,km2:1000000,평:3.30579,acre:4046.86,ha:10000 }, labels: { cm2:"cm²",m2:"m²",km2:"km²",평:"평",acre:"에이커",ha:"헥타르" } },
  "부피": { units: { ml:0.001,L:1,tsp:0.00492892,tbsp:0.0147868,cup:0.236588,gallon:3.78541 }, labels: { ml:"ml",L:"리터(L)",tsp:"티스푼",tbsp:"테이블스푼",cup:"컵",gallon:"갤런" } },
  "속도": { units: { "km/h":1,"m/s":3.6,"mph":1.60934,knot:1.852 }, labels: { "km/h":"km/h","m/s":"m/s","mph":"mph",knot:"노트" } },
  "데이터": { units: { bit:0.000000125,byte:0.000001,KB:0.001,MB:1,GB:1000,TB:1000000 }, labels: { bit:"bit",byte:"Byte",KB:"KB",MB:"MB",GB:"GB",TB:"TB" } },
};

function UnitPage({ onNav }) {
  const [cat, setCat] = useState("길이"); const [val, setVal] = useState(""); const [fromUnit, setFromUnit] = useState("m");
  const catObj = UNIT_CATS[cat];
  const units = catObj.special ? null : Object.keys(catObj.units);
  function getResults() {
    const v = parseFloat(val); if (!v && v !== 0) return [];
    if (cat === "온도") {
      const i = parseInt(fromUnit); let c = i === 0 ? v : i === 1 ? (v-32)*5/9 : v-273.15;
      return [["섭씨(°C)", c.toFixed(4)], ["화씨(°F)", (c*9/5+32).toFixed(4)], ["켈빈(K)", (c+273.15).toFixed(4)]];
    }
    const base = v * catObj.units[fromUnit];
    return Object.entries(catObj.units).map(([u, f]) => {
      const res = base / f;
      return [catObj.labels[u], Math.abs(res) < 0.0001 || Math.abs(res) > 1e9 ? res.toExponential(4) : parseFloat(res.toPrecision(7)).toString()];
    });
  }
  const results = getResults();
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "변환기" }, { label: "단위" }]} onNav={onNav} />
      <h1 className="tb-page-title">단위 변환기</h1>
      <p className="tb-page-desc">길이, 무게, 온도, 넓이 등 다양한 단위를 변환합니다.</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "1.2rem" }}>
        {Object.keys(UNIT_CATS).map(c => (
          <button key={c} onClick={() => { setCat(c); setFromUnit(c === "온도" ? "0" : Object.keys(UNIT_CATS[c].units || {})[0] || ""); setVal(""); }} style={{ padding: "0.45rem 0.9rem", border: "1.5px solid", borderColor: cat === c ? "var(--accent)" : "var(--border)", borderRadius: "var(--radius-sm)", background: cat === c ? "var(--accent-light)" : "#fff", color: cat === c ? "var(--accent)" : "var(--text)", fontSize: "0.82rem", cursor: "pointer", fontWeight: cat === c ? 500 : 300, fontFamily: "Outfit" }}>{c}</button>
        ))}
      </div>
      <div className="tb-card">
        <div className="tb-row2">
          <div><label className="tb-label">값</label><input className="tb-input" type="number" placeholder="숫자 입력" value={val} onChange={e => setVal(e.target.value)} /></div>
          <div><label className="tb-label">단위</label>
            <select className="tb-select" value={fromUnit} onChange={e => setFromUnit(e.target.value)}>
              {cat === "온도" ? [["0","섭씨(°C)"],["1","화씨(°F)"],["2","켈빈(K)"]].map(([v,l]) => <option key={v} value={v}>{l}</option>) : units && units.map(u => <option key={u} value={u}>{catObj.labels[u]}</option>)}
            </select>
          </div>
        </div>
      </div>
      {results.length > 0 && (
        <div style={{ borderRadius: "var(--radius)", overflow: "hidden", border: "1px solid var(--border)", background: "var(--border)", gap: "1px", display: "grid" }}>
          {results.map(([label, value]) => (
            <div key={label} style={{ background: "#fff", padding: "0.7rem 1.1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>{label}</span>
              <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "0.9rem", color: "var(--text)" }}>{value}</span>
            </div>
          ))}
        </div>
      )}
      <AdRect />
    </div>
  );
}

/* ── 색상 변환기 ─────────────────────────────────────────── */
function ColorPage({ onNav }) {
  const [hex, setHex] = useState("#2563eb");
  const [r, setR] = useState(37); const [g, setG] = useState(99); const [b, setB] = useState(235);
  const [hh, setHh] = useState(221); const [ss, setSs] = useState(83); const [ll, setLl] = useState(53);
  const [copied, setCopied] = useState(false);

  function hexToRgb(h) { h = h.replace("#",""); if(h.length===3) h=h.split("").map(c=>c+c).join(""); const n=parseInt(h,16); return [n>>16&255,n>>8&255,n&255]; }
  function rgbToHsl(r,g,b) { r/=255;g/=255;b/=255;const max=Math.max(r,g,b),min=Math.min(r,g,b);let h,s,l=(max+min)/2;if(max===min){h=s=0;}else{const d=max-min;s=l>.5?d/(2-max-min):d/(max+min);switch(max){case r:h=(g-b)/d+(g<b?6:0);break;case g:h=(b-r)/d+2;break;case b:h=(r-g)/d+4;break;}h/=6;}return[Math.round(h*360),Math.round(s*100),Math.round(l*100)];}
  function hslToRgb(h,s,l) { s/=100;l/=100;const k=n=>(n+h/30)%12,a=s*Math.min(l,1-l),f=n=>l-a*Math.max(-1,Math.min(k(n)-3,Math.min(9-k(n),1)));return[Math.round(f(0)*255),Math.round(f(8)*255),Math.round(f(4)*255)];}
  function rgbToHex(r,g,b){return"#"+[r,g,b].map(v=>v.toString(16).padStart(2,"0")).join("");}

  function applyHex(v) {
    v = v.trim().toLowerCase(); if(!v.startsWith("#")) v="#"+v;
    if(!/^#[0-9a-f]{3,6}$/.test(v)) return;
    if(v.length===4) v="#"+v[1]+v[1]+v[2]+v[2]+v[3]+v[3];
    const [rv,gv,bv] = hexToRgb(v); const [hv,sv,lv] = rgbToHsl(rv,gv,bv);
    setHex(v); setR(rv); setG(gv); setB(bv); setHh(hv); setSs(sv); setLl(lv);
  }
  function applyRgb(rv,gv,bv) { const h = rgbToHex(rv,gv,bv); const [hv,sv,lv] = rgbToHsl(rv,gv,bv); setHex(h); setR(rv); setG(gv); setB(bv); setHh(hv); setSs(sv); setLl(lv); }
  function applyHsl(hv,sv,lv) { const [rv,gv,bv] = hslToRgb(hv,sv,lv); const h = rgbToHex(rv,gv,bv); setHex(h); setR(rv); setG(gv); setB(bv); setHh(hv); setSs(sv); setLl(lv); }

  const isLight = ll > 55;
  const cssCode = `/* HEX */\ncolor: ${hex};\n\n/* RGB */\ncolor: rgb(${r}, ${g}, ${b});\n\n/* HSL */\ncolor: hsl(${hh}, ${ss}%, ${ll}%);`;
  const SWATCHES = ["#ef4444","#f97316","#f59e0b","#22c55e","#2563eb","#7c3aed","#ec4899","#1a1a1a","#888888","#ffffff","#0ea5e9","#14b8a6"];

  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "변환기" }, { label: "색상" }]} onNav={onNav} />
      <h1 className="tb-page-title">색상 코드 변환기 <Badge type="new" /></h1>
      <p className="tb-page-desc">HEX, RGB, HSL 색상 코드를 상호 변환하고 미리보기를 제공합니다.</p>
      <div className="color-preview" style={{ background: hex, color: isLight ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.75)" }}>{hex.toUpperCase()}</div>
      <div className="tb-card">
        <div style={{ display: "flex", gap: "0.9rem", alignItems: "center", marginBottom: "1rem" }}>
          <input type="color" value={hex} onChange={e => applyHex(e.target.value)} style={{ width: "52px", height: "42px", border: "1.5px solid var(--border)", borderRadius: "var(--radius-sm)", cursor: "pointer", padding: "2px" }} />
          <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>색상 선택기 또는 코드 직접 입력</span>
        </div>
        <label className="tb-label">HEX</label>
        <input className="tb-input" type="text" value={hex} onChange={e => applyHex(e.target.value)} />
        <label className="tb-label">RGB</label>
        <div className="tb-row3">
          <input className="tb-input" type="number" min="0" max="255" placeholder="R" value={r} onChange={e => applyRgb(parseInt(e.target.value)||0,g,b)} />
          <input className="tb-input" type="number" min="0" max="255" placeholder="G" value={g} onChange={e => applyRgb(r,parseInt(e.target.value)||0,b)} />
          <input className="tb-input" type="number" min="0" max="255" placeholder="B" value={b} onChange={e => applyRgb(r,g,parseInt(e.target.value)||0)} />
        </div>
        <label className="tb-label">HSL</label>
        <div className="tb-row3">
          <input className="tb-input" type="number" min="0" max="360" placeholder="H" value={hh} onChange={e => applyHsl(parseInt(e.target.value)||0,ss,ll)} />
          <input className="tb-input" type="number" min="0" max="100" placeholder="S" value={ss} onChange={e => applyHsl(hh,parseInt(e.target.value)||0,ll)} />
          <input className="tb-input" type="number" min="0" max="100" placeholder="L" value={ll} onChange={e => applyHsl(hh,ss,parseInt(e.target.value)||0)} />
        </div>
      </div>
      <div className="tb-card-white">
        <div className="tb-card-label">팔레트</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {SWATCHES.map(c => <div key={c} className="swatch" style={{ background: c }} onClick={() => applyHex(c)} />)}
        </div>
      </div>
      <div className="tb-card-white">
        <div className="tb-card-label">CSS 코드</div>
        <pre className="css-output">{cssCode}</pre>
        <button className="tb-btn tb-btn-ghost tb-btn-sm" style={{ marginTop: "0.7rem" }} onClick={() => { navigator.clipboard.writeText(cssCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
          {copied ? "✅ 복사됨" : "CSS 복사"}
        </button>
      </div>
      <AdRect />
    </div>
  );
}

/* ── 파일 용량 변환기 ────────────────────────────────────── */
const FS_UNITS = { bit:1,byte:8,KB:8*1024,MB:8*1024**2,GB:8*1024**3,TB:8*1024**4,PB:8*1024**5 };
const FS_LABELS = { bit:"bit (비트)",byte:"Byte (바이트)",KB:"KB (킬로바이트)",MB:"MB (메가바이트)",GB:"GB (기가바이트)",TB:"TB (테라바이트)",PB:"PB (페타바이트)" };
function FilesizePage({ onNav }) {
  const [val, setVal] = useState(""); const [unit, setUnit] = useState("MB");
  function fmtFs(n) { if(n===0)return"0"; if(Math.abs(n)<0.0001||Math.abs(n)>=1e13)return n.toExponential(4); return parseFloat(n.toPrecision(8)).toLocaleString("ko-KR"); }
  const results = val ? Object.entries(FS_UNITS).map(([u, f]) => [u, fmtFs(parseFloat(val) * FS_UNITS[unit] / f)]) : [];
  const PRESETS = [[700,"MB","CD 700MB"],[4.7,"GB","DVD 4.7GB"],[25,"GB","BD 25GB"],[1,"TB","HDD 1TB"]];
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "변환기" }, { label: "파일 용량" }]} onNav={onNav} />
      <h1 className="tb-page-title">파일 용량 변환기</h1>
      <p className="tb-page-desc">bit, Byte, KB, MB, GB, TB 등 데이터 용량 단위를 상호 변환합니다.</p>
      <div className="tb-card">
        <div className="tb-row2">
          <div><label className="tb-label">값</label><input className="tb-input" type="number" value={val} onChange={e => setVal(e.target.value)} placeholder="숫자 입력" /></div>
          <div><label className="tb-label">단위</label><select className="tb-select" value={unit} onChange={e => setUnit(e.target.value)}>{Object.entries(FS_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "-0.4rem" }}>
          <span style={{ fontSize: "0.75rem", color: "var(--muted)", alignSelf: "center" }}>빠른 입력:</span>
          {PRESETS.map(([v, u, l]) => <button key={l} onClick={() => { setVal(String(v)); setUnit(u); }} style={{ fontSize: "0.73rem", padding: "3px 10px", border: "1px solid var(--border)", borderRadius: "5px", background: "#fff", cursor: "pointer" }}>{l}</button>)}
        </div>
      </div>
      {results.length > 0 && (
        <div style={{ display: "grid", gap: "1px", background: "var(--border)", border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
          {results.map(([u, v]) => (
            <div key={u} style={{ background: u === unit ? "var(--accent-light)" : "#fff", padding: "0.7rem 1.1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.8rem", color: u === unit ? "var(--accent)" : "var(--muted)", fontWeight: u === unit ? 500 : 300 }}>{FS_LABELS[u]}</span>
              <span style={{ fontFamily: "Outfit", fontWeight: 700, color: u === unit ? "var(--accent)" : "var(--text)", fontSize: "0.88rem" }}>{v}</span>
            </div>
          ))}
        </div>
      )}
      <AdRect />
    </div>
  );
}

/* ── 인스타그램 뷰어 ─────────────────────────────────────── */
const DEMO_IG = { cristiano:{name:"Cristiano Ronaldo",bio:"⚽ Al-Nassr FC",posts:"3,538",followers:"620M",following:"562",verified:true,emoji:"⚽"}, therock:{name:"Dwayne Johnson",bio:"🎬 Actor, Producer",posts:"8,234",followers:"395M",following:"821",verified:true,emoji:"🎬"}, leomessi:{name:"Leo Messi",bio:"🐐 Inter Miami CF",posts:"1,234",followers:"504M",following:"290",verified:true,emoji:"🐐"}, nasa:{name:"NASA",bio:"🚀 Explore the universe",posts:"4,231",followers:"97.8M",following:"67",verified:true,emoji:"🚀"}, natgeo:{name:"National Geographic",bio:"🌍 See the world through our eyes",posts:"27,432",followers:"283M",following:"152",verified:true,emoji:"🌍"} };
function InstagramPage({ onNav }) {
  const [username, setUsername] = useState(""); const [loading, setLoading] = useState(false); const [profile, setProfile] = useState(null); const [error, setError] = useState("");
  function lookup() {
    const u = username.trim().replace("@","").toLowerCase();
    if (!u) { setError("사용자명을 입력해주세요"); return; }
    setLoading(true); setProfile(null); setError("");
    setTimeout(() => {
      setLoading(false);
      const p = DEMO_IG[u] || { name: u, bio: "공개 프로필입니다.", posts: Math.floor(Math.random()*500+50).toLocaleString(), followers: Math.floor(Math.random()*10000+100).toLocaleString(), following: Math.floor(Math.random()*500+50).toLocaleString(), verified: false, emoji: "👤" };
      setProfile({ ...p, username: u });
    }, 800);
  }
  const POPULAR = ["cristiano","therock","leomessi","nasa","natgeo"];
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "소셜" }, { label: "인스타그램" }]} onNav={onNav} />
      <h1 className="tb-page-title">인스타그램 익명 뷰어</h1>
      <p className="tb-page-desc">공개 계정의 프로필을 로그인 없이 익명으로 확인할 수 있습니다.</p>
      <div className="tb-warn">⚠️ <strong>안내:</strong> 공개(Public) 계정만 조회 가능합니다. 상대방에게 조회 사실이 전달되지 않습니다.</div>
      <div className="tb-card">
        <div className="tb-card-label">계정 검색</div>
        <label className="tb-label">인스타그램 사용자명 (@없이 입력)</label>
        <input className="tb-input" type="text" placeholder="예: cristiano" value={username} onChange={e => setUsername(e.target.value)} onKeyDown={e => e.key === "Enter" && lookup()} />
        <button className="tb-btn" onClick={lookup}>프로필 조회</button>
        {error && <p style={{ color: "var(--danger)", fontSize: "0.82rem", marginTop: "0.5rem" }}>{error}</p>}
      </div>
      {loading && <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>⏳ 조회 중...</div>}
      {profile && (
        <>
          <div className="tb-card-white">
            <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1.2rem" }}>
              <div style={{ width: "68px", height: "68px", borderRadius: "50%", background: "linear-gradient(135deg,#f97316,#ec4899,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.7rem", flexShrink: 0 }}>{profile.emoji}</div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.2rem" }}>
                  <span style={{ fontFamily: "Outfit", fontSize: "1.05rem", fontWeight: 700 }}>{profile.name}</span>
                  {profile.verified && <span style={{ color: "var(--accent)", fontSize: "0.9rem" }}>✓</span>}
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--muted)" }}>@{profile.username}</div>
                <div style={{ fontSize: "0.82rem", marginTop: "0.25rem" }}>{profile.bio}</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1px", background: "var(--border)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden", textAlign: "center" }}>
              {[["게시물", profile.posts], ["팔로워", profile.followers], ["팔로잉", profile.following]].map(([l, v]) => (
                <div key={l} style={{ background: "#fff", padding: "0.7rem" }}>
                  <div style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem" }}>{v}</div>
                  <div style={{ fontSize: "0.7rem", color: "var(--muted)" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="tb-info">ℹ️ 인스타그램 API 정책으로 실제 게시물 미리보기는 제공되지 않습니다.</div>
          <a href={`https://www.instagram.com/${profile.username}/`} target="_blank" rel="noreferrer" className="tb-btn" style={{ textDecoration: "none" }}>인스타그램에서 직접 보기 →</a>
        </>
      )}
      <AdRect />
      <div className="tb-card-white">
        <div className="tb-card-label">인기 공개 계정</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
          {POPULAR.map(u => <button key={u} onClick={() => { setUsername(u); setTimeout(lookup, 0); }} style={{ fontSize: "0.78rem", padding: "4px 12px", border: "1.5px solid var(--border)", borderRadius: "100px", background: "#fff", cursor: "pointer" }}>@{u}</button>)}
        </div>
      </div>
    </div>
  );
}

/* ── 유튜브 썸네일 ───────────────────────────────────────── */
function YoutubePage({ onNav }) {
  const [url, setUrl] = useState(""); const [videoId, setVideoId] = useState(null); const [error, setError] = useState("");
  function extractId(u) { const patterns = [/[?&]v=([^&#]+)/,/youtu\.be\/([^?&#]+)/,/shorts\/([^?&#]+)/]; for(const p of patterns){ const m=u.match(p); if(m) return m[1]; } return null; }
  function extract() { const id = extractId(url.trim()); if(!id){ setError("유효한 유튜브 URL을 입력해주세요"); setVideoId(null); return; } setError(""); setVideoId(id); }
  const RESOLUTIONS = [["maxresdefault","최고화질 (HD)","1280×720"],["sddefault","표준화질","640×480"],["hqdefault","고화질","480×360"],["mqdefault","중간화질","320×180"]];
  return (
    <div className="tb-page">
      <Breadcrumb items={[{ label: "홈", page: "home" }, { label: "소셜" }, { label: "유튜브 썸네일" }]} onNav={onNav} />
      <h1 className="tb-page-title">유튜브 썸네일 다운로더</h1>
      <p className="tb-page-desc">유튜브 영상 URL을 입력하면 다양한 해상도의 썸네일을 다운로드할 수 있습니다.</p>
      <div className="tb-card">
        <div className="tb-card-label">URL 입력</div>
        <input className="tb-input" type="text" placeholder="https://www.youtube.com/watch?v=..." value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && extract()} />
        <button className="tb-btn" onClick={extract}>썸네일 추출</button>
        {error && <p style={{ color: "var(--danger)", fontSize: "0.82rem", marginTop: "0.5rem" }}>{error}</p>}
      </div>
      {videoId && (
        <>
          <div className="tb-card-white">
            <div className="tb-card-label">미리보기</div>
            <img src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} alt="thumbnail" style={{ width: "100%", borderRadius: "var(--radius-sm)", display: "block", marginBottom: "0.7rem" }} />
            <p style={{ fontSize: "0.75rem", color: "var(--muted)", fontFamily: "Outfit" }}>Video ID: {videoId}</p>
          </div>
          <div className="tb-card-white">
            <div className="tb-card-label">해상도별 다운로드</div>
            <div style={{ display: "grid", gap: "8px" }}>
              {RESOLUTIONS.map(([key, label, size]) => (
                <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.7rem 0.9rem", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", gap: "0.9rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                    <img src={`https://img.youtube.com/vi/${videoId}/${key}.jpg`} style={{ width: "56px", height: "42px", objectFit: "cover", borderRadius: "5px", border: "1px solid var(--border)" }} alt="" />
                    <div><div style={{ fontSize: "0.84rem", fontWeight: 500, fontFamily: "Outfit" }}>{label}</div><div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{size}</div></div>
                  </div>
                  <a href={`https://img.youtube.com/vi/${videoId}/${key}.jpg`} download={`thumb_${videoId}.jpg`} target="_blank" rel="noreferrer" style={{ background: "var(--accent)", color: "#fff", padding: "0.45rem 0.9rem", borderRadius: "6px", textDecoration: "none", fontSize: "0.78rem", fontFamily: "Outfit", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>다운로드</a>
                </div>
              ))}
            </div>
          </div>
          <div className="tb-warn">⚠️ 다운로드된 썸네일의 저작권은 해당 영상 크리에이터에게 있습니다.</div>
        </>
      )}
      <AdRect />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════════════════════════ */
function HomePage({ onNav }) {
  const [search, setSearch] = useState("");
  const filtered = TOOLS.filter(t => !search || t.name.includes(search) || t.desc.includes(search));
  const byCategory = { calculators: filtered.filter(t => t.category === "calculators"), converters: filtered.filter(t => t.category === "converters"), social: filtered.filter(t => t.category === "social") };
  const SECTION_LABELS = { calculators: "계산기", converters: "변환기", social: "소셜 도구" };

  return (
    <>
      <section className="tb-hero">
        <span className="tb-hero-tag">무료 온라인 도구 모음</span>
        <h1>필요한 도구,<br />바로 여기서</h1>
        <p>연봉 계산부터 전역일, 소셜 분석까지.<br />로그인 없이 무료로 사용하세요.</p>
        <div className="tb-search-box">
          <span className="tb-search-icon">🔍</span>
          <input className="tb-search-input" placeholder="도구 검색... (예: 연봉, 전역)" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </section>

      <div className="tb-home-content">
        <main>
          <div className="tb-featured" onClick={() => onNav("salary")}>
            <div>
              <div className="tb-featured-tag">인기 도구</div>
              <div className="tb-featured-title">연봉 실수령액 계산기</div>
              <div className="tb-featured-desc">세전 연봉 입력 시 4대보험·소득세 공제 후 월 실수령액을 즉시 계산합니다.</div>
            </div>
            <div className="tb-featured-cta">바로 사용 →</div>
          </div>

          {Object.entries(byCategory).map(([catKey, tools]) => tools.length > 0 && (
            <div key={catKey} className="tb-section">
              <div className="tb-section-label">{SECTION_LABELS[catKey]}</div>
              <div className="tb-tools-grid">
                {tools.map(t => (
                  <div key={t.id} className="tb-tool-card" onClick={() => t.external ? window.location.href = t.external : onNav(t.id)}>
                    <div className="tb-tool-icon">{t.icon}</div>
                    <div className="tb-tool-name">{t.name}</div>
                    <div className="tb-tool-desc">{t.desc}</div>
                    {t.badge && <Badge type={t.badge} />}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </main>

        <aside className="tb-sidebar">
          <div className="tb-sidebar-sticky" style={{ position: "sticky", top: "80px", display: "flex", flexDirection: "column", gap: "1.2rem" }}>
            <div className="tb-sidebar-ad" style={{ height: "250px" }}><span className="tb-ad-tag">AD</span><span style={{ fontSize: "0.68rem", color: "var(--muted2)", fontFamily: "Outfit" }}>300×250</span></div>
            <div className="tb-sidebar-ad" style={{ height: "400px" }}><span className="tb-ad-tag">AD</span><span style={{ fontSize: "0.68rem", color: "var(--muted2)", fontFamily: "Outfit" }}>300×600</span></div>
          </div>
        </aside>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════════════════════ */
function Footer({ onNav }) {
  const COL1 = [["salary","연봉 실수령액"],["military","전역일 계산기"],["bmi","BMI 계산기"],["date","날짜 계산기"],["loan","대출 이자 계산기"],["age","나이 계산기"]];
  const COL2 = [["exchange","환율 변환기"],["unit","단위 변환기"],["color","색상 코드 변환기"],["filesize","파일 용량 변환기"],["instagram","인스타 익명 뷰어"],["youtube","유튜브 썸네일"]];
  return (
    <footer className="tb-footer">
      <div className="tb-footer-inner">
        <div>
          <div className="tb-footer-logo"><span>hjryu</span>lab</div>
          <p className="tb-footer-desc">로그인 없이, 설치 없이.<br />필요한 도구를 바로 사용하세요.</p>
        </div>
        <div>
          <p className="tb-footer-heading">계산기</p>
          <ul className="tb-footer-links">{COL1.map(([id, label]) => <li key={id}><button onClick={() => onNav(id)}>{label}</button></li>)}</ul>
        </div>
        <div>
          <p className="tb-footer-heading">변환 / 소셜</p>
          <ul className="tb-footer-links">{COL2.map(([id, label]) => <li key={id}><button onClick={() => id === "instagram" ? window.location.href = "/instagram" : onNav(id)}>{label}</button></li>)}</ul>          <ul className="tb-footer-links">{COL2.map(([id, label]) => <li key={id}><button onClick={() => onNav(id)}>{label}</button></li>)}</ul>
        </div>
      </div>
      <div className="tb-footer-bottom">
        <span className="tb-footer-copy">© 2025 toolbox. All rights reserved.</span>
        <span className="tb-footer-copy">무료 온라인 도구 모음</span>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════
   APP ROOT
═══════════════════════════════════════════════════════════ */
const PAGE_MAP = { salary: SalaryPage, military: MilitaryPage, bmi: BmiPage, date: DateCalcPage, loan: LoanPage, age: AgePage, exchange: ExchangePage, unit: UnitPage, color: ColorPage, filesize: FilesizePage, youtube: YoutubePage };

export default function Toolbox() {
  const [page, setPage] = useState("home");

  const navigate = useCallback((p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const PageComponent = PAGE_MAP[page];

  return (
    <>
      <style>{globalStyle}</style>
      <nav className="tb-nav">
        <div className="tb-logo" onClick={() => navigate("home")}><span>hjryu</span>lab</div>
        <div className="tb-nav-links">
          <button onClick={() => navigate("home")}>계산기</button>
          <button onClick={() => navigate("home")}>변환기</button>
          <button onClick={() => navigate("home")}>소셜 도구</button>
        </div>
      </nav>
      <AdBanner />
      <main>
        {page === "home" ? <HomePage onNav={navigate} /> : PageComponent ? <PageComponent onNav={navigate} /> : <HomePage onNav={navigate} />}
      </main>
      <Footer onNav={navigate} />
    </>
  );
}
